/* Upsell brand popup -> GA4 (Shopify.analytics.publish -> custom web pixel).
 *
 * Popup side (any PDP that renders the popup):
 *   tigermedical:ubp_view       popup became visible
 *   tigermedical:ubp_close      dismissed without following a link
 *   tigermedical:ubp_cta_click  followed a link to the upsell product
 *
 * Funnel side (the upsell PDP the CTA points at):
 *   tigermedical:ubp_view_item     landed on it inside the attribution window
 *   tigermedical:ubp_add_to_cart   added it from that page
 *
 * A CTA click arms a 30-minute sliding window in localStorage. Only the upsell
 * PDP itself is in the funnel, and only its own product form counts, so quick
 * adds from related-product tiles never get attributed. Every qualifying
 * /cart/add gets hidden `_ubp_*` line-item properties so the order carries the
 * source into checkout, where the pixel turns it into a purchase event. */
(function () {
  'use strict';

  if (window.__ubpAnalytics) return;
  window.__ubpAnalytics = true;

  const NS = 'tigermedical:';
  const SOURCE = 'upsell_brand_popup';

  const STATE_KEY = '_ubpAttribution';
  const WINDOW_MS = 30 * 60 * 1000; // sliding, refreshed by funnel activity only
  const MAX_LIFETIME_MS = 12 * 60 * 60 * 1000; // absolute cap from the CTA click
  const TRANSIT_MS = 5 * 60 * 1000; // grace for click -> landing
  const ADD_INTENT_MS = 10 * 1000; // grace for ATC click -> /cart/add request
  const CLOSE_AFTER_CTA_MS = 1500;
  const PUBLISH_TIMEOUT_MS = 15 * 1000;

  // Set to false to stop writing hidden line-item properties (purchase
  // attribution stops working; every other event keeps working).
  const TAG_LINE_ITEMS = true;
  const PROP_SOURCE = '_ubp_source';
  const PROP_FROM = '_ubp_from';
  const PROP_EL = '_ubp_el';

  const CART_ADD = /\/cart\/add(\.js)?($|\?)/;
  const ATC_BUTTON = '[data-add-to-sidecart], [data-product-atc], .product-form--atc-button, button[type="submit"], input[type="submit"]';
  const OWN_FORM = '[data-product-form], .product-sticky-atc';
  const FOREIGN_PRODUCT = '.productitem, .custom-related-item, .comparable-product, .custom-sidecart, .custom-sidecart-section, .productitem-quickshop, [data-modal-container], [data-modal-container-1]';

  /* ------------------------------ publishing ------------------------------ */

  const pending = [];
  let pendingTimer = null;

  const canPublish = () => !!(window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function');

  function emit(name, payload) {
    try { window.Shopify.analytics.publish(name, payload); } catch (e) {}
  }

  // Shopify.analytics comes from content_for_header and may not be parsed yet.
  function publish(name, payload) {
    const full = NS + name;
    const data = payload || {};
    if (canPublish()) { emit(full, data); return; }
    pending.push([full, data]);
    if (pending.length > 20) pending.shift();
    if (pendingTimer) return;
    const deadline = Date.now() + PUBLISH_TIMEOUT_MS;
    pendingTimer = setInterval(function () {
      if (canPublish()) {
        while (pending.length) {
          const ev = pending.shift();
          emit(ev[0], ev[1]);
        }
      }
      if (!pending.length || Date.now() > deadline) {
        clearInterval(pendingTimer);
        pendingTimer = null;
        pending.length = 0;
      }
    }, 200);
  }

  /* -------------------------------- helpers ------------------------------- */

  function str(v) {
    const s = v == null ? '' : String(v);
    return s.length > 100 ? s.slice(0, 100) : s;
  }

  function num(v) {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return isFinite(n) ? n : 0;
  }

  const round2 = (v) => Math.round(num(v) * 100) / 100;

  function currency() {
    try { return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || ''; } catch (e) { return ''; }
  }

  const device = () => (window.innerWidth && window.innerWidth < 768 ? 'mobile' : 'desktop');

  function handleFromPath(pathname) {
    const m = /\/products\/([^/?#]+)/.exec(pathname || '');
    if (!m) return '';
    try { return decodeURIComponent(m[1]).toLowerCase(); } catch (e) { return m[1].toLowerCase(); }
  }

  function referrerPath() {
    try {
      if (!document.referrer) return null;
      const u = new URL(document.referrer, location.href);
      return u.origin === location.origin ? u.pathname || '' : null;
    } catch (e) { return null; }
  }

  let configCache = null;
  function config() {
    if (configCache) return configCache;
    try {
      const el = document.getElementById('ubp-analytics-config');
      const cfg = el ? JSON.parse(el.textContent || 'null') : null;
      if (cfg && cfg.handle) configCache = cfg;
    } catch (e) {}
    return configCache;
  }

  /* ---------------------------- attribution state ------------------------- */

  function writeState(s) {
    try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch (e) {}
    return s;
  }

  function clearState() {
    try { localStorage.removeItem(STATE_KEY); } catch (e) {}
  }

  function readState() {
    let s = null;
    try { s = JSON.parse(localStorage.getItem(STATE_KEY) || 'null'); } catch (e) { return null; }
    if (!s || s.src !== SOURCE || !s.handle || !s.t || !s.t0) return null;
    const now = Date.now();
    if (now - s.t >= WINDOW_MS || now - s.t0 >= MAX_LIFETIME_MS) { clearState(); return null; }
    return s;
  }

  function touch(s) {
    s.t = Date.now();
    writeState(s);
  }

  function arm(cfg, el) {
    const now = Date.now();
    writeState({
      src: SOURCE,
      t0: now,
      t: now,
      handle: String(cfg.handle).toLowerCase(),
      pid: str(cfg.product_id),
      vid: str(cfg.variant_id),
      title: str(cfg.title),
      price: round2(cfg.price),
      vendor: str(cfg.vendor),
      type: str(cfg.type),
      parent: str(cfg.parent_handle),
      pp: location.pathname,
      el: el
    });
  }

  // A landing belongs to the popup when it happens right after the click, or
  // when the referrer is still the page the popup was shown on. Reaching the
  // same product later by a direct link does not qualify.
  function arrivedFromPopup(s) {
    if (s.ok) return true;
    if (Date.now() - s.t0 < TRANSIT_MS) return true;
    const ref = referrerPath();
    return !!(ref && s.pp && ref === s.pp);
  }

  /* ------------------------------- popup side ----------------------------- */

  let lastCtaAt = 0;
  let closeMethod = '';

  function popupParams(cfg, extra) {
    const p = {
      upsell_source: SOURCE,
      parent_handle: str(cfg.parent_handle),
      upsell_handle: str(cfg.handle),
      upsell_product_id: str(cfg.product_id),
      upsell_title: str(cfg.title),
      upsell_price: round2(cfg.price),
      parent_price: round2(cfg.parent_price),
      save_percent: num(cfg.save_percent),
      leadtime_faster: num(cfg.leadtime_faster),
      currency: currency() || str(cfg.currency),
      device: device()
    };
    if (extra) for (const k in extra) p[k] = extra[k];
    return p;
  }

  function watchPopup() {
    const popup = document.getElementById('upsell-brand-popup');
    const cfg = config();
    if (!popup || !cfg || typeof MutationObserver === 'undefined') return;

    let open = false;
    let openedAt = 0;

    const sync = function () {
      const isOpen = popup.classList.contains('is-active');
      if (isOpen === open) return;
      open = isOpen;
      if (open) {
        openedAt = Date.now();
        publish('ubp_view', popupParams(cfg));
        return;
      }
      const visible = openedAt ? Date.now() - openedAt : 0;
      openedAt = 0;
      if (Date.now() - lastCtaAt < CLOSE_AFTER_CTA_MS) return;
      publish('ubp_close', popupParams(cfg, { close_method: closeMethod || 'unknown', visible_ms: visible }));
      closeMethod = '';
    };

    new MutationObserver(sync).observe(popup, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  function onPopupPointer(e) {
    if (e.type === 'auxclick' && e.button !== 1) return;
    const target = e.target;
    if (!target || !target.closest || !target.closest('#upsell-brand-popup')) return;

    if (target.closest('[data-modal-close]')) {
      closeMethod = target.closest('.upsell-brand-popup__overlay') ? 'overlay' : 'button';
      return;
    }

    const link = target.closest('a[href]');
    const cfg = config();
    if (!link || !cfg) return;

    let destination = '';
    try { destination = handleFromPath(new URL(link.getAttribute('href'), location.origin).pathname); } catch (err) {}
    if (!destination || destination !== String(cfg.handle).toLowerCase()) return;

    const el = link.classList.contains('upsell-brand-popup__btn') ? 'button'
      : link.classList.contains('upsell-brand-popup__product-image') ? 'image' : 'link';

    lastCtaAt = Date.now();
    arm(cfg, el);
    publish('ubp_cta_click', popupParams(cfg, { click_element: el }));
  }

  function onEscape(e) {
    if (e.key === 'Escape' || e.keyCode === 27) closeMethod = 'escape';
  }

  /* ------------------------------ funnel side ----------------------------- */

  const state = readState();
  const inFunnel = !!state && handleFromPath(location.pathname) === state.handle && arrivedFromPopup(state);

  if (inFunnel) {
    state.ok = 1;
    touch(state);
  }

  function liveVariant() {
    const out = { id: '', price: null };
    try {
      const input = document.querySelector('[data-product-form] [name="id"]');
      if (input && input.value) out.id = String(input.value);
      if (!out.id) {
        const selection = document.querySelector('variant-selection[variant]');
        if (selection) out.id = String(selection.getAttribute('variant') || '');
      }
      const meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      const variants = meta && Array.isArray(meta.variants) ? meta.variants : null;
      if (variants) {
        let hit = null;
        for (let i = 0; i < variants.length; i++) {
          if (!out.id || String(variants[i].id) === out.id) { hit = variants[i]; break; }
        }
        if (hit) {
          if (!out.id) out.id = String(hit.id);
          if (typeof hit.price === 'number') out.price = hit.price / 100;
        }
      }
    } catch (e) {}
    return out;
  }

  function publishViewItem(s) {
    const variant = liveVariant();
    const price = variant.price != null ? variant.price : num(s.price);
    publish('ubp_view_item', {
      upsell_source: SOURCE,
      parent_handle: str(s.parent),
      upsell_handle: str(s.handle),
      click_element: str(s.el),
      currency: currency(),
      value: round2(price),
      items: [{
        item_id: variant.id || str(s.vid),
        item_name: str(s.title),
        item_brand: str(s.vendor),
        item_category: str(s.type),
        price: round2(price),
        quantity: 1
      }]
    });
  }

  let addIntentAt = 0;
  const addPending = () => !!addIntentAt && Date.now() - addIntentAt < ADD_INTENT_MS;

  // The request body cannot tell products apart, so a main-form click arms the
  // flag and the next /cart/add claims it. Clicking add-to-cart on any other
  // product clears it, so a main-form click that failed validation and never
  // reached the network cannot leak onto that product's request.
  function markAddIntent(el) {
    if (!el || !el.closest) return;
    addIntentAt = el.closest(OWN_FORM) && !el.closest(FOREIGN_PRODUCT) ? Date.now() : 0;
  }

  function onAddClick(e) {
    const target = e.target;
    if (!target || !target.closest) return;
    const button = target.closest(ATC_BUTTON);
    if (button) markAddIntent(button);
  }

  function onAddSubmit(e) {
    const form = e.target;
    if (form && form.matches && form.matches('form')) markAddIntent(form);
  }

  function inspectBody(body) {
    if (!body) return null;
    if (typeof FormData !== 'undefined' && body instanceof FormData) return { kind: 'form', fd: body };
    if (typeof body !== 'string') return null;
    const trimmed = body.replace(/^\s+/, '');
    if (trimmed.charAt(0) === '{') {
      let obj;
      try { obj = JSON.parse(trimmed); } catch (e) { return null; }
      return obj && typeof obj === 'object' ? { kind: 'json', obj: obj } : null;
    }
    if (body.indexOf('=') === -1) return null;
    try { return { kind: 'query', sp: new URLSearchParams(body) }; } catch (e) { return null; }
  }

  function lineProperties(s) {
    const out = {};
    out[PROP_SOURCE] = SOURCE;
    if (s.parent) out[PROP_FROM] = s.parent;
    if (s.el) out[PROP_EL] = s.el;
    return out;
  }

  function assign(bag, props) {
    for (const k in props) if (!(k in bag)) bag[k] = props[k];
  }

  function tag(body, s) {
    const info = inspectBody(body);
    if (!info) return null;
    const props = lineProperties(s);

    if (info.kind === 'json') {
      if (Array.isArray(info.obj.items)) {
        info.obj.items.forEach(function (item) {
          if (!item) return;
          item.properties = item.properties || {};
          assign(item.properties, props);
        });
      } else {
        info.obj.properties = info.obj.properties || {};
        assign(info.obj.properties, props);
      }
      return JSON.stringify(info.obj);
    }

    if (info.kind === 'query') {
      for (const k in props) {
        const key = 'properties[' + k + ']';
        if (!info.sp.has(key)) info.sp.append(key, props[k]);
      }
      return info.sp.toString();
    }

    for (const k in props) {
      const key = 'properties[' + k + ']';
      if (info.fd.get(key) == null) info.fd.append(key, props[k]);
    }
    return info.fd;
  }

  // /cart/add answers with the added line item(s). A cart-shaped answer would
  // list everything already in the cart, so skip it rather than over-report.
  function addedLines(data) {
    if (!data || typeof data !== 'object') return [];
    if (typeof data.item_count === 'number' || typeof data.total_price === 'number' || typeof data.token === 'string') return [];
    return Array.isArray(data.items) ? data.items : [data];
  }

  function publishAdd(data, s) {
    const lines = addedLines(data);
    const items = [];
    let value = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (!line || (!line.variant_id && !line.id)) continue;
      const quantity = num(line.quantity) || 1;
      const unit = num(typeof line.final_price === 'number' ? line.final_price : line.price) / 100;
      const total = typeof line.final_line_price === 'number' ? line.final_line_price / 100 : unit * quantity;
      value += total;
      items.push({
        item_id: str(line.variant_id || line.id),
        item_name: str(line.product_title || line.title),
        item_variant: str(line.variant_title) || undefined,
        item_brand: str(line.vendor) || undefined,
        item_category: str(line.product_type) || undefined,
        price: round2(unit),
        quantity: quantity,
        index: i
      });
    }

    if (!items.length) return;

    publish('ubp_add_to_cart', {
      upsell_source: SOURCE,
      parent_handle: str(s.parent),
      upsell_handle: str(s.handle),
      click_element: str(s.el),
      currency: currency(),
      value: round2(value),
      items: items
    });
  }

  function patchFetch() {
    if (!window.fetch || window.__ubpFetchPatched) return;
    window.__ubpFetchPatched = true;
    const original = window.fetch.bind(window);

    window.fetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (method === 'POST' && CART_ADD.test(url) && init && init.body && addPending()) {
          const s = readState();
          if (s) {
            addIntentAt = 0;
            touch(s);
            const next = {};
            for (const k in init) next[k] = init[k];
            const tagged = TAG_LINE_ITEMS ? tag(init.body, s) : null;
            if (tagged != null) next.body = tagged;
            const request = original(input, next);
            request.then(function (response) {
              if (!response || !response.ok) return;
              response.clone().json().then(function (data) { publishAdd(data, s); }).catch(function () {});
            }).catch(function () {});
            return request;
          }
        }
      } catch (e) {}
      return original(input, init);
    };
  }

  function patchXhr() {
    if (!window.XMLHttpRequest || window.__ubpXhrPatched) return;
    window.__ubpXhrPatched = true;
    const proto = window.XMLHttpRequest.prototype;
    const open = proto.open;
    const send = proto.send;

    proto.open = function (method, url) {
      try {
        this.__ubpMethod = String(method || '').toUpperCase();
        this.__ubpUrl = String(url || '');
      } catch (e) {}
      return open.apply(this, arguments);
    };

    proto.send = function (body) {
      let out = body;
      try {
        if (this.__ubpMethod === 'POST' && CART_ADD.test(this.__ubpUrl || '') && body && addPending()) {
          const s = readState();
          if (s) {
            addIntentAt = 0;
            touch(s);
            const tagged = TAG_LINE_ITEMS ? tag(body, s) : null;
            if (tagged != null) out = tagged;
            const xhr = this;
            xhr.addEventListener('load', function () {
              try {
                if (xhr.status < 200 || xhr.status >= 300) return;
                publishAdd(JSON.parse(xhr.responseText), s);
              } catch (e) {}
            });
          }
        }
      } catch (e) { out = body; }
      return send.call(this, out);
    };
  }

  /* --------------------------------- boot -------------------------------- */

  document.addEventListener('click', onPopupPointer, true);
  document.addEventListener('auxclick', onPopupPointer, true);
  document.addEventListener('keydown', onEscape, true);

  if (inFunnel) {
    document.addEventListener('click', onAddClick, true);
    document.addEventListener('submit', onAddSubmit, true);
    patchFetch();
    patchXhr();
  }

  function boot() {
    watchPopup();
    if (inFunnel) publishViewItem(state);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
